import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,

  useState,
} from 'react';

/**
 * MapOrbTrayLayout — single source of truth for where the MapOrbTray
 * currently occupies the viewport. MapOrbTray itself self-registers via
 * useRegisterMapOrbTray(); any other component that shares the canvas
 * (SkillTreeViewport, MoleculeMap, future overlays) subscribes via
 * useMapOrbTrayRect() to reserve clearance without hard-coding the
 * tray's pixel dimensions.
 *
 * The tray is position:absolute, bottom:20px inside .deck-map-area, so
 * its height + bottom-offset change as the user pins/unpins favorites.
 * A ResizeObserver keeps the published rect in sync.
 *
 * Returns DOMRect (live coords in CSS pixels) or null when the tray
 * isn't mounted. Consumers should treat null as "no occlusion known".
 */

const MapOrbTrayLayoutContext = createContext({
  rect: null,
  registerTrayElement: () => () => undefined,
});

export function MapOrbTrayLayoutProvider({ children }) {
  const [rect, setRect] = useState(null);

  const registerTrayElement = useCallback((element) => {
    if (!element) {
      setRect(null);
      return () => undefined;
    }
    const update = () => setRect(element.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(element);
    // The tray is position:absolute, bottom:20px relative to its
    // offsetParent (the dashboard slot). When the SLOT resizes — e.g.
    // .deck-map-area grows from 280px to 520px when skill-tree mode
    // activates — the tray's viewport position shifts but its OWN size
    // doesn't change, so ResizeObserver on the tray alone misses it.
    // Observe the offsetParent too so any container reflow re-publishes.
    if (element.offsetParent) ro.observe(element.offsetParent);
    // Also catch viewport-relative shifts from page scroll / window resize.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      setRect(null);
    };
  }, []);

  const value = useMemo(
    () => ({ rect, registerTrayElement }),
    [rect, registerTrayElement],
  );

  return (
    <MapOrbTrayLayoutContext.Provider value={value}>
      {children}
    </MapOrbTrayLayoutContext.Provider>
  );
}

/** Subscribe to the published MapOrbTray rect (CSS pixels). null when unmounted. */
export function useMapOrbTrayRect() {
  return useContext(MapOrbTrayLayoutContext).rect;
}

/**
 * Attach a ref to the MapOrbTray root element; the layout context will
 * keep its rect in sync via ResizeObserver. Idempotent across re-mounts.
 */
export function useRegisterMapOrbTray(ref) {
  const { registerTrayElement } = useContext(MapOrbTrayLayoutContext);
  useEffect(() => {
    return registerTrayElement(ref.current);
  }, [ref, registerTrayElement]);
}
